import React from 'react';
import { cn } from './cn';

type ButtonVariant =
  | 'default' | 'ghost' | 'outline' | 'secondary' | 'destructive' | 'link'
  | 'danger-soft' | 'long' | 'short';
type ButtonSize = 'default' | 'sm' | 'xs' | 'lg' | 'icon' | 'icon-sm';

/**
 * У каждой кнопки четыре состояния, и все четыре описаны здесь явно:
 * покой → hover → нажатие (active) → выключена. Раньше нажатие нигде не
 * отличалось от наведения, и на тач-экране было непонятно, дошёл тап или нет.
 *
 * Цвета — только токены темы. Сырые bg-white / bg-emerald-* ломались при
 * переключении светлой и тёмной темы: белая кнопка на почти-чёрном фоне
 * выжигала глаз, а emerald жил своей жизнью мимо --trade-green.
 */
const variantClasses: Record<ButtonVariant, string> = {
  default:     'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
  secondary:   'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70',
  outline:     'border border-border bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/70',
  ghost:       'text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent/70',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
  link:        'text-primary underline-offset-4 hover:underline',
  // Опасное, но не главное действие (Flatten ALL, удалить): красный контур
  // вместо красной заливки — заливка притягивает палец сильнее, чем следует.
  'danger-soft': 'border border-trade-red/40 bg-trade-red/10 text-trade-red hover:bg-trade-red/20 active:bg-trade-red/30',
  long:        'bg-trade-green text-white hover:opacity-90 active:opacity-80',
  short:       'bg-trade-red text-white hover:opacity-90 active:opacity-80',
};

// Кегль кнопок держим на 14/13px: 12px и 11px, стоявшие тут раньше, делали
// панель действий мельче всего остального на экране — кнопка выглядела
// подписью, а не кнопкой.
// На телефоне всё не ниже 40px — под палец (32-36px приходилось выцеливать);
// с 640px кнопки возвращаются к плотным десктопным размерам.
const sizeClasses: Record<ButtonSize, string> = {
  default:   'h-10 sm:h-9 px-4 py-2',
  sm:        'h-10 sm:h-9 rounded-md px-3.5 text-sm',
  xs:        'h-10 sm:h-8 rounded-md px-3 text-[13px]',
  lg:        'h-11 sm:h-10 rounded-md px-8',
  icon:      'h-10 w-10 sm:h-9 sm:w-9',
  'icon-sm': 'h-10 w-10 sm:h-8 sm:w-8 rounded-md',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
